// Mosh-style predictive local echo. On a high-latency link, typed characters
// only appear after a full SSH round-trip (the server echoes them). This shows
// each typed character INSTANTLY as a dim "ghost" at the cursor while we wait
// for the real echo, which then replaces it.
//
// The ghost is a plain DOM overlay positioned over xterm's `.xterm-screen` — it
// NEVER writes into xterm's buffer, so it can't corrupt the screen or double
// characters (worst case the ghost is briefly wrong and is cleared when real
// output arrives). xterm's `registerDecoration` API can't be used because it's
// disabled in the alternate buffer, and tmux keeps xterm in the alt buffer.
//
// Safety: the client can't read the PTY's ECHO flag, so predictions are gated by
// CONFIRMATION (Mosh-style). We predict only once the context has proven it
// echoes keystrokes literally — i.e. a keystroke was followed by a short,
// control-free echo. A TUI (agent CLI / vim / a slow `sllm claude` waiting
// screen) redraws instead of echoing, so confirmation fails and we stop; a
// password prompt (no echo) is gated too. We start unconfirmed.

import type { Terminal } from '@xterm/xterm';

const NO_ECHO_MS = 700;
const PASSWORD_RE = /(password|passphrase)\b.*:\s*$/i;
const ANSI_RE = /\x1b\[[0-9;?]*[ -/]*[@-~]/g;

// A "simple echo" is a short run of printable bytes with no control characters
// (no ESC / CR / LF / BS / DEL) — exactly what a shell emits when it echoes
// typed keys. A TUI redraw or a prompt/line repaint is not simple, so it never
// confirms — which is how we keep predictions out of full-screen apps.
function isSimpleEcho(b: Uint8Array): boolean {
    if (b.length === 0 || b.length > 8) return false;
    for (let i = 0; i < b.length; i++) {
        const c = b[i]!;
        if (c < 0x20 || c === 0x7f) return false;
    }
    return true;
}

export class LocalEcho {
    private readonly term: Terminal;
    private readonly ghost: HTMLDivElement;
    private screen: HTMLElement | null = null;
    private readonly decoder = new TextDecoder();
    private pending = '';
    private confident = false; // has the context proven it echoes keystrokes?
    private awaiting = false; // a keystroke is awaiting its echo (to confirm)
    private timer: ReturnType<typeof setTimeout> | null = null;
    private inputAt = 0; // performance.now() of the keystroke awaiting echo
    private readonly onLatency?: (ms: number) => void;

    constructor(term: Terminal, onLatency?: (ms: number) => void) {
        this.term = term;
        this.onLatency = onLatency;
        const el = document.createElement('div');
        el.setAttribute('aria-hidden', 'true');
        el.style.cssText =
            'position:absolute;display:none;pointer-events:none;white-space:pre;' +
            'z-index:5;opacity:0.5;color:var(--text-faint,#6b7385);';
        this.ghost = el;
    }

    /** A character the user just typed (from `term.onData`), pre-echo. */
    input(d: string): void {
        // Predict only single printable characters. Enter, backspace, control
        // and escape sequences (arrow keys, etc.) and pastes clear the ghost.
        const code = d.length === 1 ? d.charCodeAt(0) : -1;
        const printable = code >= 0x20 && code !== 0x7f;
        if (!printable) {
            // Enter / backspace / control / escape sequences / pastes.
            this.clear();
            this.awaiting = false;
            this.inputAt = 0;
            return;
        }
        // Expect an echo so serverData can (dis)confirm the context.
        this.awaiting = true;
        this.inputAt = performance.now();
        if (!this.confident) {
            // Learn first — don't ghost until the context is confirmed to echo.
            this.clear();
            return;
        }
        this.pending += d;
        this.render();
        // Backstop: if no echo arrives within ~1 RTT, drop the stale ghost.
        if (this.timer) clearTimeout(this.timer);
        this.timer = setTimeout(() => this.clear(), NO_ECHO_MS);
    }

    /** Server output arrived — the real echo supersedes any prediction. */
    serverData(bytes: Uint8Array): void {
        this.clear();
        const tail = this.decoder
            .decode(bytes, { stream: true })
            .replace(ANSI_RE, '')
            .slice(-256);
        if (PASSWORD_RE.test(tail)) {
            this.confident = false;
            this.awaiting = false;
            return;
        }
        // Confirm/deny the context using the echo that follows a keystroke: a
        // simple literal echo → a shell that echoes (safe to predict); anything
        // else (a TUI redraw with escapes/positioning, a multi-line repaint) →
        // stop predicting until a literal echo proves otherwise again.
        if (this.awaiting) {
            this.awaiting = false;
            this.confident = isSimpleEcho(bytes);
            // Opportunistic keystroke→server-echo latency sample. Chunks that
            // bundle the echo with other output fail isSimpleEcho and simply
            // yield no sample — fine, the next clean echo will. The 5s cap
            // guards against a swallowed keystroke (read -s, raw-mode TUI)
            // leaving a stale inputAt that a much-later unrelated short chunk
            // would otherwise report as an hours-long "echo latency".
            if (this.confident && this.inputAt > 0) {
                const elapsed = performance.now() - this.inputAt;
                if (elapsed < 5000) this.onLatency?.(elapsed);
            }
            this.inputAt = 0;
        }
    }

    /** Hide any current ghost (e.g. on resize/scroll). Keeps predictions on. */
    clear(): void {
        if (this.timer) clearTimeout(this.timer);
        this.timer = null;
        this.pending = '';
        this.ghost.style.display = 'none';
    }

    dispose(): void {
        if (this.timer) clearTimeout(this.timer);
        this.timer = null;
        this.ghost.remove();
        this.screen = null;
        this.pending = '';
    }

    // Lazily attach the ghost to `.xterm-screen` (only exists after term.open)
    // and copy the terminal's font so it lines up cell-for-cell.
    private mount(): boolean {
        if (this.screen?.isConnected) return true;
        const screen = this.term.element?.querySelector(
            '.xterm-screen',
        ) as HTMLElement | null;
        if (!screen) return false;
        this.screen = screen;
        const cs = getComputedStyle(screen);
        this.ghost.style.fontFamily = cs.fontFamily;
        this.ghost.style.fontSize = cs.fontSize;
        if (this.ghost.parentElement !== screen) screen.appendChild(this.ghost);
        return true;
    }

    private render(): void {
        if (!this.mount() || !this.screen) {
            this.pending = '';
            return;
        }
        const cols = this.term.cols || 1;
        const rows = this.term.rows || 1;
        const cellW = this.screen.clientWidth / cols;
        const cellH = this.screen.clientHeight / rows;
        const buf = this.term.buffer.active;
        this.ghost.textContent = this.pending;
        this.ghost.style.left = `${buf.cursorX * cellW}px`;
        this.ghost.style.top = `${buf.cursorY * cellH}px`;
        this.ghost.style.height = `${cellH}px`;
        this.ghost.style.lineHeight = `${cellH}px`;
        this.ghost.style.display = 'block';
    }
}
